import {observable} from 'mobx';

import {AbstractStore, ApiCollection, PagedCollection, SplitViewCollection} from 'modelx-jts-common';

import AccountingDocumentIdModel  from '../models/AccountingDocumentIdModel';
import SupplierApiCollection      from '../models/collections/SupplierApiCollection';
import AccountingDocumentVatModel from '../models/AccountingDocumentVatModel';
import ExpensePaymentVoucherModel      from '../models/ExpensePaymentVoucherModel';
import AccountingDocumentDeadlineModel from '../models/AccountingDocumentDeadlineModel';
import AccountingDocumentHistoryModel from '../models/AccountingDocumentHistoryModel';
import ExpenseBudgetModel             from '../models/ExpenseBudgetModel';
import AccountingDocumentExpenseModel from '../models/AccountingDocumentExpenseModel';
import ProjectModel                   from '../models/ProjectModel';
import ProjectSubCategoryModel        from '../models/ProjectSubCategoryModel';
import ProjectCategoryModel           from '../models/ProjectCategoryModel';
import ProjectPoleModel               from '../models/ProjectPoleModel';
import AccountingDocumentBudgetModel  from '../models/AccountingDocumentBudgetModel';
import ExpenseGroupModel              from '../models/ExpenseGroupModel';

const getUniqueIds = (ids: any[]) => [...new Set(ids.filter(id => id))];

// Un filtre vide n'est pas envoyé à l'API, ce qui listerait toute la collection : on vide simplement
const listByIds = (collection: ApiCollection, ids: any[]) => {
	if (!ids.length) {
		collection.clear();
		return Promise.resolve();
	}

	return collection.setFilter('id', ids).list();
};

class AccountingDocumentsSplitViewStore extends AbstractStore {

	@observable
	public currentAccountingDocument = new AccountingDocumentBudgetModel();

	@observable
	public splitViewCollection = new SplitViewCollection(AccountingDocumentBudgetModel, AccountingDocumentIdModel);

	@observable
	public supplierCollection = new SupplierApiCollection();

	@observable
	public accountingDocumentAccountingDocumentExpenseCollection = new ApiCollection(AccountingDocumentExpenseModel);

	@observable
	public accountingDocumentDeadlineCollection = new ApiCollection(AccountingDocumentDeadlineModel);

	@observable
	public accountingDocumentExpenseCollection = new ApiCollection(ExpenseBudgetModel);

	@observable
	public accountingDocumentHistoryCollection = new PagedCollection(AccountingDocumentHistoryModel);

	@observable
	public accountingDocumentVatCollection = new ApiCollection(AccountingDocumentVatModel);

	@observable
	public accountingDocumentVoucherCollection = new ApiCollection(ExpensePaymentVoucherModel);

	public expenseCollection = new ApiCollection(ExpenseBudgetModel);
	public voucherCollection = new ApiCollection(ExpensePaymentVoucherModel);
	public projectCollection = new ApiCollection(ProjectModel);
	public subCategoryCollection = new ApiCollection(ProjectSubCategoryModel);
	public categoryCollection = new ApiCollection(ProjectCategoryModel);
	public poleCollection = new ApiCollection(ProjectPoleModel);
	public expenseGroupCollection = new ApiCollection(ExpenseGroupModel);

	constructor() {
		super();

		this.setCurrentAccountingDocument = this.setCurrentAccountingDocument.bind(this);
	}

	public setCurrentAccountingDocument(accountingDocument: AccountingDocumentBudgetModel): Promise<any> {
		this.accountingDocumentAccountingDocumentExpenseCollection.setFilter(
			'accountingDocument',
			accountingDocument.get('@id')
		);

		this.accountingDocumentDeadlineCollection.setFilter(
			'accountingDocument',
			accountingDocument.get('@id')
		);
		this.accountingDocumentDeadlineCollection.addSort('deadline', true);

		this.accountingDocumentExpenseCollection.setFilter(
			'accountingDocumentExpenses.accountingDocument',
			accountingDocument.get('@id')
		);

		this.accountingDocumentHistoryCollection.setFilter(
			'accountingDocument',
			accountingDocument.get('@id')
		);
		this.accountingDocumentHistoryCollection.addSort('createdAt', false);

		this.accountingDocumentVatCollection.setFilter('accountingDocument', accountingDocument.get('@id'));
		this.accountingDocumentVatCollection.addSort('vat.rate', false);

		this.accountingDocumentVoucherCollection.setFilter(
			'expensePaymentVoucherAccountingDocumentExpenses.accountingDocumentExpense.accountingDocument',
			accountingDocument.get('@id')
		);

		const promises = [
			this.accountingDocumentAccountingDocumentExpenseCollection.list(),
			this.accountingDocumentDeadlineCollection.list(),
			this.accountingDocumentExpenseCollection.list(),
			this.accountingDocumentHistoryCollection.list(),
			this.accountingDocumentVatCollection.list(),
			this.accountingDocumentVoucherCollection.list(),
		];

		return Promise
			.all(promises)
			.then(() => {
				this.currentAccountingDocument = accountingDocument;
				return true;
			});
	}

	public loadExpenseCollection(accountingDocumentId) {
		this.expenseCollection.setFilter(
			'accountingDocumentExpenses.accountingDocument',
			accountingDocumentId
		);

		return this.expenseCollection.list();
	}

	public loadVoucherCollection(accountingDocumentId) {
		this.voucherCollection.setFilter(
			'expensePaymentVoucherAccountingDocumentExpenses.accountingDocumentExpense.accountingDocument',
			accountingDocumentId
		);

		return this.voucherCollection.list();
	}

	// Charge uniquement les entités de l'organigramme référencées par les dépenses de la pièce
	// (à appeler après loadExpenseCollection), niveau par niveau car l'API n'imbrique qu'un niveau
	public async loadOrganigramEntitiesFromExpenses() {
		const projectIds = getUniqueIds(this.expenseCollection.map(expense => expense.get('project.id')));
		await listByIds(this.projectCollection, projectIds);

		const subCategoryIds = getUniqueIds(this.projectCollection.map(project => project.get('projectSubCategory.id')));
		const expenseGroupIds = getUniqueIds(this.projectCollection.map(project => project.get('expenseGroup.id')));
		await Promise.all([
			listByIds(this.subCategoryCollection, subCategoryIds),
			listByIds(this.expenseGroupCollection, expenseGroupIds),
		]);

		const categoryIds = getUniqueIds(this.subCategoryCollection.map(subCategory => subCategory.get('projectCategory.id')));
		await listByIds(this.categoryCollection, categoryIds);

		const poleIds = getUniqueIds(this.categoryCollection.map(category => category.get('projectPole.id')));
		await listByIds(this.poleCollection, poleIds);
	}
}

export default new AccountingDocumentsSplitViewStore();
